// Per-product click tracking for the member portal.
//
//     product_click — the member opened the product (card, title or nutrition chip)
//     product_add   — added straight from the card
//
// Same event names, same metadata keys and the same "value is the product
// handle" rule as the storefront (wildgrain-sleek-theme/snippets/statsig-product-clicks.liquid)
// and the box builder (wildgrain-bundle .../lib/productTracking.js). Only
// `surface` differs.
//
// There is deliberately no impression event: a viewability beacon on every card
// was an order of magnitude more traffic than the clicks it gave a denominator
// to. These are raw counts, not a rate — ranking on them ranks by popularity,
// and a product shown in more rows will out-rank a better one shown in fewer.
//
// Identity: the portal runs on app.wildgrain.com, so it shares no cookie (and
// therefore no stableID) with the storefront. Every visitor here is logged in
// though, so the join back to storefront activity is the Shopify customer id —
// the same userID the theme's statsig-init.liquid sets. We start with the
// Recharge customer id from the stored session (available immediately, on the
// very first render) and upgrade to the Shopify id via identifyMember() as soon
// as subscriptions resolve.

import { StatsigClient } from '@statsig/js-client';
import { identifyMemberInClarity } from './clarityTags';

// Same Statsig project as the storefront theme, so portal and website events
// land in one place and a product's score can be compared across surfaces.
const CLIENT_KEY =
  process.env.REACT_APP_STATSIG_CLIENT_KEY ||
  'client-TlQ9JQtuUl2D7uoISdVTzE4XqdDvJQMjwE68C5fRWoj';

const ENVIRONMENT = process.env.REACT_APP_STATSIG_ENVIRONMENT || 'production';

let clientPromise = null;
let client = null;

const str = (v) => (v == null ? '' : String(v));

const readSession = () => {
  try {
    const raw = localStorage.getItem('rechargeSession');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const rechargeCustomerId = () => {
  const session = readSession();
  if (!session) return '';
  return str(session.customer_id || session.customerId || session.customer?.id);
};

const buildUser = (overrides = {}) => {
  const rechargeId = rechargeCustomerId();
  return {
    userID: overrides.shopifyCustomerId || rechargeId || undefined,
    email: overrides.email || undefined,
    custom: {
      recharge_customer_id: rechargeId,
      surface: 'member_portal',
    },
  };
};

/**
 * Initialize the Statsig client. Safe to call repeatedly — the SDK is created
 * once and every later call reuses the same promise.
 *
 * Failure is deliberately silent: analytics must never keep a member from
 * managing their subscription, so a blocked CDN, an ad blocker or a bad key
 * degrades to "no events logged" rather than a broken page.
 */
export const initProductTracking = () => {
  if (clientPromise) return clientPromise;
  if (typeof window === 'undefined') return Promise.resolve(null);

  clientPromise = (async () => {
    try {
      const instance = new StatsigClient(CLIENT_KEY, buildUser(), {
        environment: { tier: ENVIRONMENT },
      });
      await instance.initializeAsync();
      client = instance;
      return instance;
    } catch (error) {
      console.warn('Statsig product tracking unavailable:', error?.message || error);
      return null;
    }
  })();

  return clientPromise;
};

/**
 * Re-key the Statsig user once the member's Shopify id / email are known.
 * Call it when subscriptions resolve:
 *
 *   identifyMember({
 *     shopifyCustomerId: sub?.include?.customer?.external_customer_id?.ecommerce,
 *     email: sub?.include?.customer?.email,
 *   })
 */
export const identifyMember = async ({ shopifyCustomerId, email } = {}) => {
  if (!shopifyCustomerId && !email) return;
  // Clarity gets the Shopify id only (hashed client-side), never the email.
  identifyMemberInClarity(shopifyCustomerId);
  const instance = await initProductTracking();
  if (!instance) return;
  try {
    await instance.updateUserAsync(buildUser({
      shopifyCustomerId: str(shopifyCustomerId),
      email: str(email),
    }));
  } catch {
    // Non-fatal: events keep logging under the Recharge id.
  }
};

const log = (name, value, metadata) => {
  const emit = (instance) => {
    if (instance) instance.logEvent(name, value, metadata);
  };
  if (client) emit(client);
  else initProductTracking().then(emit).catch(() => {});
};

// Handle is the join key across all three surfaces; SKU and id are for drilling
// into a specific variant afterwards.
export const productKey = (product) =>
  str(product?.handle || product?.variants?.[0]?.sku || product?.sku || product?.id);

// Which rail/grid a card sits in. The browse UI renders ProductCard from ~20
// call sites, so rather than thread a prop through every one, a container can
// declare itself with data-wg-section="…" and every card inside it inherits
// that name. An explicit `section` in the context still wins.
const sectionFromElement = (ref) => {
  const el = ref && ref.current;
  if (!el || !el.closest) return '';
  const container = el.closest('[data-wg-section]');
  return container ? container.getAttribute('data-wg-section') || '' : '';
};

export const productMetadata = (product, context = {}) => {
  const variant = product?.variants?.[0] || {};
  return {
    product_id: str(product?.id),
    handle: str(product?.handle),
    sku: str(product?.sku || variant.sku),
    title: str(product?.title),
    price: str(variant.price ?? product?.price ?? ''),
    surface: 'member_portal',
    section: str(context.section) || sectionFromElement(context.element),
    position: str(context.position),
    is_addon: context.isAddon ? 'true' : 'false',
    in_box: context.inBox ? 'true' : 'false',
    url: window.location.pathname,
  };
};

export const logProductClick = (product, context = {}) => {
  const key = productKey(product);
  if (!key) return;
  log('product_click', key, {
    ...productMetadata(product, context),
    click_target: str(context.clickTarget || 'card'),
  });
};

export const logProductAdd = (product, context = {}) => {
  const key = productKey(product);
  if (!key) return;
  log('product_add', key, {
    ...productMetadata(product, context),
    quantity: str(context.quantity ?? 1),
  });
};
